import { api } from '@/lib/api-client'
import type { ProductionCatalog } from './production-catalog'

export function hubContainerCode(value: string): string | null {
  const code = value.trim()
  if (code.startsWith('SFH1:C:')) return code
  const match = /^https?:\/\/[^/?#@\s]+\/production\/containers\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/.exec(code)
  return match ? `SFH1:C:${match[1]}` : null
}

export interface ProductionCapabilities {
  mode: 'off' | 'shadow' | 'active'
  enabled: boolean
  can_manage: boolean
}

export interface Workstation {
  id: string
  name: string
}

export interface ProductionJob {
  id: string
  production_order: string
  order_line: number
  component_line: number
  produced_item: string
  material_item: string
  operation: string
  location: string
  consumption_uom: string
  production_uom: string
  description: string
  produced_description?: string | null
  expected_quantity?: string | null
  remaining_quantity?: string | null
  planned_output?: string | null
  qty_per_unit_of_measure?: string | null
}

export interface ProductionLot {
  lot: string
  quantity_available: string
  updated_at: string
  container_reference: string | null
  container_quantity: string | null
}

export interface ProductionQuantityCheck {
  expected_consumption: string | null
  actual_consumption: string
  deviation: boolean
  consumption_uom: string
}

export interface ProductionProgress {
  confirmed_consumption: string
  draft_consumption: string
  total_consumption: string
  confirmed_output: string
  open_drafts: number
  remaining_expected: string | null
  remaining_snapshot_estimate: string | null
  remaining_output: string | null
}

export interface ProductionSessionSummary {
  id: string
  job_id: string
  status: 'draft' | 'confirmed'
  revision: number
  origin_mode: string
  produced_quantity: string | null
  production_order: string
  material_item: string
}

export interface ProductionLine {
  id: string
  scan_id: string
  lot: string
  container_reference: string
  container_source: 'scanned' | 'declared'
  quantity: string
}

export interface ProductionSession extends ProductionSessionSummary {
  output_containers: ProductionOutputContainer[]
  consumption_uom: string
  production_uom: string
  lines: ProductionLine[]
  summary: {
    consumed_quantity: string
    material_per_output_unit: string | null
  }
  integration_status: 'not_acknowledged' | 'imported' | 'posted' | 'rejected'
  erp_reference: string | null
  correction_of: string | null
  reason: string | null
}

export interface ProductionOutputContainer {
  id: string
  sequence: number
  lot: string
  quantity: string
  unit: string
  item: string
  description: string
  qr_payload: string
  session_id: string
  production_order: string
  operation: string
  origin_mode: string
}

export interface ProductionGenealogy extends ProductionOutputContainer {
  available_quantity: string
  allocation: 'session'
  sessions: {
    session_id: string
    production_order: string
    operation: string
    item: string
    produced_quantity: string
    production_unit: string
    consumption_unit: string
    consumptions: { lot: string; container_reference: string; quantity: string; item: string }[]
  }[]
}

export interface AddProductionLine {
  expected_revision: number
  scan_id: string
  lot: string
  container_reference: string
  container_source: 'scanned' | 'declared'
  quantity: string
  allow_reuse: boolean
}

export type ProductionCommand =
  | { kind: 'create'; body: { id: string; job_id: string; device_id: string } }
  | { kind: 'add'; sessionId: string; body: AddProductionLine }
  | { kind: 'update'; sessionId: string; lineId: string; body: { expected_revision: number; quantity: string } }
  | { kind: 'remove'; sessionId: string; lineId: string; body: { expected_revision: number } }
  | { kind: 'confirm'; sessionId: string; body: { expected_revision: number; produced_quantity: string; container_quantities?: string[] } }

const root = '/api/production'
const segment = encodeURIComponent
const sessionPath = (id: string) => `${root}/sessions/${segment(id)}`

async function workstationPages<T>(path: string, workstationId: string): Promise<T[]> {
  const items: T[] = []
  const limit = 200
  for (let offset = 0; ; offset += limit) {
    const page = (await api.get<T[]>(path, {
      params: { workstation_id: workstationId, offset, limit },
    })).data
    items.push(...page)
    if (page.length < limit) return items
  }
}

export const productionApi = {
  resolveLot: async (jobId: string, code: string) =>
    (await api.get<ProductionLot[]>(`${root}/jobs/${segment(jobId)}/lots/resolve`, { params: { code } })).data,
  quantityCheck: async (sessionId: string, producedQuantity: string) =>
    (await api.get<ProductionQuantityCheck>(`${sessionPath(sessionId)}/quantity-check`, {
      params: { produced_quantity: producedQuantity },
    })).data,
  resolveContainer: async (jobId: string, code: string) =>
    (await api.get<ProductionGenealogy>(`${root}/jobs/${segment(jobId)}/containers/resolve`, { params: { code } })).data,
  genealogy: async (id: string) =>
    (await api.get<ProductionGenealogy>(`${root}/containers/${segment(id)}/genealogy`)).data,
  label: async (id: string) =>
    (await api.get<Blob>(`${root}/containers/${segment(id)}/label.pdf`, { responseType: 'blob' })).data,
  importCatalog: async (catalog: ProductionCatalog) =>
    (await api.post<unknown>(`${root}/admin/catalog`, catalog)).data,
  capabilities: async () => (await api.get<ProductionCapabilities>(`${root}/capabilities`)).data,
  workstations: async () => (await api.get<Workstation[]>(`${root}/workstations`)).data,
  jobs: (workstationId: string) => workstationPages<ProductionJob>(`${root}/jobs`, workstationId),
  progress: async (jobId: string, sessionId?: string) =>
    (await api.get<ProductionProgress>(`${root}/jobs/${segment(jobId)}/progress`, {
      params: sessionId ? { session_id: sessionId } : {},
    })).data,
  lots: async (jobId: string) =>
    (await api.get<ProductionLot[]>(`${root}/jobs/${segment(jobId)}/lots`)).data,
  sessions: (workstationId: string) =>
    workstationPages<ProductionSessionSummary>(`${root}/sessions`, workstationId),
  session: async (id: string) => (await api.get<ProductionSession>(sessionPath(id))).data,
  execute: async (command: ProductionCommand): Promise<ProductionSession> => {
    switch (command.kind) {
      case 'create':
        return (await api.post<ProductionSession>(`${root}/sessions`, command.body)).data
      case 'add':
        return (await api.post<ProductionSession>(`${sessionPath(command.sessionId)}/lines`, command.body)).data
      case 'update':
        return (await api.put<ProductionSession>(
          `${sessionPath(command.sessionId)}/lines/${segment(command.lineId)}`, command.body,
        )).data
      case 'remove':
        return (await api.post<ProductionSession>(
          `${sessionPath(command.sessionId)}/lines/${segment(command.lineId)}/remove`, command.body,
        )).data
      case 'confirm':
        return (await api.post<ProductionSession>(`${sessionPath(command.sessionId)}/confirm`, command.body)).data
    }
  },
}
